import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { AccessProfile, AdminPermission } from '@/types/auth';
import { accessControlService } from '@/services';

const emptyDraft = {
  code: '',
  name: '',
  description: '',
  permission_codes: [] as string[],
};

export default function AccessProfiles() {
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [editing, setEditing] = useState<AccessProfile | null>(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const groupedPermissions = useMemo(() => permissions.reduce<Record<string, AdminPermission[]>>(
    (groups, permission) => {
      (groups[permission.category] ||= []).push(permission);
      return groups;
    },
    {},
  ), [permissions]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [profileItems, permissionItems] = await Promise.all([
        accessControlService.getProfiles(),
        accessControlService.getPermissions(),
      ]);
      setProfiles(profileItems);
      setPermissions(permissionItems);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail || 'Не удалось загрузить профили доступа');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const beginCreate = () => {
    setEditing(null);
    setDraft(emptyDraft);
    setError('');
    setOpen(true);
  };

  const beginEdit = (profile: AccessProfile) => {
    setEditing(profile);
    setDraft({
      code: profile.code,
      name: profile.name,
      description: profile.description || '',
      permission_codes: profile.permission_codes,
    });
    setError('');
    setOpen(true);
  };

  const togglePermission = (code: string) => {
    setDraft((current) => ({
      ...current,
      permission_codes: current.permission_codes.includes(code)
        ? current.permission_codes.filter((item) => item !== code)
        : [...current.permission_codes, code],
    }));
  };

  const save = async () => {
    setError('Изменение профилей через HTTP API отключено');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Профили доступа</Typography>
          <Typography color="text.secondary" mt={0.5}>
            Только просмотр. Изменения выполняются контролируемой SQL-операцией.
          </Typography>
        </Box>
        <Chip label="Только просмотр" />
      </Box>
      {error && !open && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Код</TableCell>
              <TableCell>Разрешения</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id}>
                <TableCell>
                  <Typography fontWeight={600}>{profile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{profile.description || '—'}</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={profile.code} /></TableCell>
                <TableCell>{profile.permission_codes.length}</TableCell>
                <TableCell align="right">
                  <Button size="small" disabled>Только просмотр</Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && profiles.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">Профили не созданы</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={false} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Настроить профиль' : 'Новый профиль'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
          <Stack spacing={2} mt={1}>
            <TextField
              label="Код"
              value={draft.code}
              disabled={Boolean(editing?.is_system)}
              onChange={(event) => setDraft((current) => ({ ...current, code: event.target.value.toUpperCase() }))}
              helperText="Латинские заглавные буквы, цифры и подчёркивание"
            />
            <TextField label="Название" value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
            <TextField label="Описание" value={draft.description} multiline minRows={2} onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))} />
            {Object.entries(groupedPermissions).map(([category, items]) => (
              <Paper key={category} variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={700} mb={1}>{category}</Typography>
                <Stack>
                  {items.map((permission) => (
                    <FormControlLabel
                      key={permission.code}
                      control={<Checkbox checked={draft.permission_codes.includes(permission.code)} onChange={() => togglePermission(permission.code)} />}
                      label={<Box><Typography>{permission.name}</Typography><Typography variant="caption" color="text.secondary">{permission.description}</Typography></Box>}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
